import * as babylon from "@babylonjs/core";

import { BabylonState } from "../../state";

/**
 * Solves the 'distant origin' problem by centering the scene around
 * the point the camera is looking at.
 *
 * Distant origin arises from the float64 JS numbers being converted to
 * float32 precision in the gpu shaders.
 *
 * 64-bit floats can accurately represent about 1e15 decimal places,
 * allowing them to model 1e9km down to millimeter precision.
 * (This is ~6.7AU, eg the solar system out to Jupiter)
 *
 * 32-bit floats can only represent about 1e6-1e7 decimal places,
 * so they can only model a region less than 10km across to millimeter precision.
 *
 * For an example of what it looks like when looking at objects
 * far from the origin, see here (photosensitivity warning!):
 * https://www.reddit.com/r/Unity3D/comments/148ctmb/dont_forget_floating_origin_if_youre_working_with/
 */
export function applyRenderOrigin(state: BabylonState) {
  const origin = findRenderOrigin(state);

  // Position all meshes based on the render origin.
  for (const entity of Object.values(state.entities)) {
    if (!entity.position) {
      continue;
    }
    if (entity.mesh) {
      entity.mesh.mesh.position =
        entity.position.current.vector.subtract(origin);
    }
    if (entity.path?.mesh) {
      entity.path.mesh.position = entity.position.mean.subtract(origin);
    }
    if (entity.vectorField?.mesh) {
      entity.vectorField.mesh.position = entity.position.mean.subtract(origin);
    }
    if (entity.vectorField?.currentMesh) {
      entity.vectorField.currentMesh.position =
        entity.position.current.vector.subtract(origin);
    }
  }

  // Place the grid relative to the camera's focused object.
  const grid = state.sceneConfig.grid;
  if (grid && grid.spec.scale && grid.mesh) {
    const y = (() => {
      if (grid.spec.verticalOffset !== undefined) {
        return grid.spec.verticalOffset;
      }
      const target = state.sceneConfig.camera?.spec?.target;
      if (!target) {
        return 0;
      }
      const targetEntity = state.entities[target.id];
      if (
        !targetEntity?.position ||
        !targetEntity.position.cache.allPoints[0]
      ) {
        return 0;
      }
      return targetEntity.position.cache.allPoints[0].y;
    })();
    if (y !== grid.verticalOffset) {
      // TODO: broadcast this computed value to the react state
      // to be able to show the default value in the panel settings.
      grid.verticalOffset = y;
    }
    const gridDataPosition = new babylon.Vector3(
      origin.x - (origin.x % grid.spec.scale),
      y,
      origin.z - (origin.z % grid.spec.scale),
    );
    grid.mesh.position = gridDataPosition.subtract(origin);
  }
}

/**
 * Gets the point in data-space to use as (0,0,0) for render-space.
 */
function findRenderOrigin(state: BabylonState): babylon.Vector3 {
  // Set a default value.
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return babylon.Vector3.Zero();
  }
  const target = camera.spec.target;
  const targetEntity = state.entities[target.id];
  if (!targetEntity?.position || !targetEntity[target.mesh]) {
    return babylon.Vector3.Zero();
  }
  return target.mesh === "mesh"
    ? targetEntity.position.current.vector.clone()
    : targetEntity.position.mean.clone();
}

export const RENDER_INTERVAL_MS = 1000 / 60;
